import { useEffect, useMemo, useRef, useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import ActionButtons from "../../components/common/ActionButtons";
import FeatureToggleGrid from "../../components/common/FeatureToggleGrid";
import { useStoreFeatures } from "../../hooks/useStoreFeatures";
import {
  useGetAccessLevelsQuery,
  useGetUserScreensQuery,
  useCreateAccessLevelMutation,
  useUpdateAccessLevelMutation,
  useDeleteAccessLevelMutation,
} from "../../store/api";

const normalizeText = (value) => {
  if (value === undefined || value === null) return "";
  return String(value).trim();
};

const normalizeIdList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => normalizeText(item)).filter(Boolean);
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => normalizeText(item)).filter(Boolean);
      }
    } catch {
      // ignore parse errors and fall through to comma split
    }
    return value
      .split(",")
      .map((item) => normalizeText(item))
      .filter(Boolean);
  }
  return [];
};

const SCREEN_LABEL_BY_ROUTE = {
  "/pos": "Point of Sale",
  "/orders/live": "Live Orders",
  "/orders/history": "Order History",
  "/floor": "Tables & Floor Plan",
  "/reservations": "Reservations",
  "/cash-drawer": "Cash Drawer",
  "/day-close": "Day Close & Cashing Up",
  "/admin/users": "User & PIN Management",
  "/admin/access-levels": "Access Levels",
  "/admin/audit": "Audit Log",
  "/devices/health": "Device Health",
  "/devices/local-devices": "Local Devices",
  "/devices/kiosks": "Kiosks",
  "/devices/terminals": "Terminals",
  "/config/printers": "Printers",
  "/config/devices": "Devices & License",
  "/config/pos": "POS Settings",
  "/config/kds": "KDS Settings",
  "/config/kiosk": "Kiosk Settings",
  "/config/branding": "Store Branding",
  "/config/payments": "Payment Terminals",
  "/reports/sales": "Sales Reports",
  "/reports/staff": "Staff Performance",
  "/reports/financial": "Financial & Z-Reports",
  "/catalog/items": "Menu Items",
  "/catalog/categories": "Menu Categories",
  "/catalog/modifiers": "Modifiers",
  "/pricing/promotions": "Promotions & Discounts",
  "/pricing/tax": "Tax & Fees",
  "/menu/station-assignment": "Station Assignment",
  "/fulfillment/pending-payments": "Pending Payments",
};

const toTitleCase = (value) =>
  String(value)
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const getPrettyScreenLabel = (screen) => {
  const name = normalizeText(screen?.name || screen?.displayName || screen?.label);
  if (name) return name;

  const route = normalizeText(screen?.route).toLowerCase();
  if (route && SCREEN_LABEL_BY_ROUTE[route]) {
    return SCREEN_LABEL_BY_ROUTE[route];
  }

  const screenKey = normalizeText(screen?.screenKey);
  if (screenKey) {
    return toTitleCase(screenKey.replace(/[_/]+/g, " "));
  }

  if (route) {
    const lastSegment = route.split("/").filter(Boolean).pop() || route;
    return toTitleCase(lastSegment.replace(/[-_]+/g, " "));
  }

  return "";
};

const getScreenId = (screen) =>
  normalizeText(
    screen?.appOptionId ||
      screen?.id ||
      screen?.screenKey ||
      screen?.route ||
      "",
  );

const getRequiredFeatureKey = (screen) => {
  const route = normalizeText(screen?.route).toLowerCase();
  const screenKey = normalizeText(screen?.screenKey).toUpperCase();

  if (!route && !screenKey) return null;

  if (
    route.startsWith("/pos") ||
    route.startsWith("/orders/") ||
    route === "/floor" ||
    route.startsWith("/reservations") ||
    route.startsWith("/cash-drawer") ||
    route.startsWith("/day-close") ||
    screenKey === "POS"
  ) {
    return "enablePos";
  }

  if (route.startsWith("/config/kiosk") || screenKey.includes("KIOSK")) {
    return "enableKiosk";
  }

  if (route.startsWith("/config/kds") || screenKey.includes("KDS")) {
    return "enableKds";
  }

  if (
    route.startsWith("/config/payments") ||
    screenKey.includes("PAYMENT") ||
    screenKey.includes("ONLINE")
  ) {
    return "enableOnlinePayments";
  }

  if (route.startsWith("/reports/")) {
    return "enablePos";
  }

  return null;
};

const isScreenAvailableForBusiness = (screen, features) => {
  const requiredFeature = getRequiredFeatureKey(screen);
  if (!requiredFeature) return true;
  return Boolean(features?.[requiredFeature]);
};

const sortByOrderThenName = (a, b) => {
  const orderDiff = Number(a?.sortOrder ?? 0) - Number(b?.sortOrder ?? 0);
  if (orderDiff !== 0) return orderDiff;
  return normalizeText(a?.name).localeCompare(normalizeText(b?.name));
};

const AccessLevels = () => {
  const { data, isLoading, isError, error } = useGetAccessLevelsQuery();
  const {
    data: screensData,
    isLoading: screensLoading,
    isFetching: screensFetching,
    isError: screensError,
    error: screensQueryError,
  } = useGetUserScreensQuery();
  const storeFeaturesState = useStoreFeatures(true);
  const features = storeFeaturesState.features;
  const featuresLoading =
    storeFeaturesState.isLoading || storeFeaturesState.isFetching;
  const featuresError = storeFeaturesState.isError;

  const [createAccessLevel, createState] = useCreateAccessLevelMutation();
  const [updateAccessLevel, updateState] = useUpdateAccessLevelMutation();
  const [deleteAccessLevel] = useDeleteAccessLevelMutation();

  const [form, setForm] = useState({
    name: "",
    description: "",
    sortOrder: 0,
    isActive: true,
  });
  const [selectedAppOptionIds, setSelectedAppOptionIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    accessLevelId: null,
    accessLevelName: "",
  });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });
  const saveSuccessTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (saveSuccessTimeoutRef.current) {
        clearTimeout(saveSuccessTimeoutRef.current);
      }
    };
  }, []);

  const accessLevels = useMemo(
    () => (Array.isArray(data) ? data : []),
    [data],
  );
  const allScreens = useMemo(
    () => (Array.isArray(screensData) ? screensData : []),
    [screensData],
  );

  const visibleScreens = useMemo(() => {
    if (!allScreens.length) return [];
    return allScreens.filter((screen) =>
      isScreenAvailableForBusiness(screen, features),
    );
  }, [allScreens, features]);

  const visibleScreenIds = useMemo(
    () =>
      visibleScreens
        .map((screen) => getScreenId(screen))
        .filter(Boolean),
    [visibleScreens],
  );

  const hiddenScreenCount = useMemo(() => {
    if (!allScreens.length || !features) return 0;
    return allScreens.length - visibleScreens.length;
  }, [allScreens.length, features, visibleScreens.length]);

  const screenLookup = useMemo(() => {
    const lookup = new Map();
    visibleScreens.forEach((screen) => {
      lookup.set(getScreenId(screen), screen);
    });
    return lookup;
  }, [visibleScreens]);

  const screenTree = useMemo(() => {
    const groupsById = new Map();
    const rootGroups = [];
    const standaloneScreens = [];

    visibleScreens
      .slice()
      .sort(sortByOrderThenName)
      .forEach((screen) => {
        const screenId = getScreenId(screen);
        if (!screenId) return;

        if (!normalizeText(screen?.route)) {
          const group = {
            parent: screen,
            children: [],
          };
          groupsById.set(screenId, group);
          rootGroups.push(group);
          return;
        }

        if (!screen?.parentId) {
          standaloneScreens.push(screen);
        }
      });

    visibleScreens.forEach((screen) => {
      const parentId = normalizeText(screen?.parentId);
      if (!parentId) return;

      const group = groupsById.get(parentId);
      if (!group) {
        standaloneScreens.push(screen);
        return;
      }

      group.children.push(screen);
    });

    rootGroups.forEach((group) => {
      group.children.sort(sortByOrderThenName);
    });

    return {
      groups: rootGroups.filter((group) => group.children.length > 0),
      standaloneScreens: standaloneScreens.sort(sortByOrderThenName),
    };
  }, [visibleScreens]);

  useEffect(() => {
    if (!showForm) return;
    if (!editingId) return;

    const currentAccessLevel = accessLevels.find(
      (accessLevel) => accessLevel.accessLevelId === editingId,
    );
    if (!currentAccessLevel) return;

    setSelectedAppOptionIds(
      normalizeIdList(
        currentAccessLevel.appOptionIds ||
          currentAccessLevel.permissions ||
          currentAccessLevel.screenIds,
      ),
    );
  }, [accessLevels, editingId, showForm]);

  useEffect(() => {
    if (!showForm) {
      createState.reset();
      updateState.reset();
    }
  }, [createState, showForm, updateState]);

  const resetForm = () => {
    createState.reset();
    updateState.reset();
    setEditingId(null);
    setForm({ name: "", description: "", sortOrder: 0, isActive: true });
    setSelectedAppOptionIds([]);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleToggleScreen = (event) => {
    const screenId = normalizeText(event?.target?.name);
    const checked = Boolean(event?.target?.checked);
    if (!screenId) return;

    setSelectedAppOptionIds((prev) => {
      if (checked) {
        return prev.includes(screenId) ? prev : [...prev, screenId];
      }
      return prev.filter((id) => id !== screenId);
    });
  };

  const handleSelectAllScreens = () => {
    setSelectedAppOptionIds((prev) => {
      const mergedIds = new Set([
        ...prev,
        ...visibleScreenIds,
      ]);
      return Array.from(mergedIds);
    });
  };

  const handleClearAllScreens = () => {
    setSelectedAppOptionIds([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const isUpdating = Boolean(editingId);
      const payload = {
        name: form.name,
        description: form.description,
        sortOrder: Number(form.sortOrder),
        isActive: form.isActive,
        appOptionIds: selectedAppOptionIds,
      };

      if (editingId) {
        await updateAccessLevel({
          accessLevelId: editingId,
          payload,
        }).unwrap();
        setEditingId(null);
      } else {
        await createAccessLevel(payload).unwrap();
      }

      createState.reset();
      updateState.reset();
      setSaveSuccessMessage(
        `Access level ${isUpdating ? "updated" : "created"} successfully.`,
      );
      if (saveSuccessTimeoutRef.current) {
        clearTimeout(saveSuccessTimeoutRef.current);
      }
      saveSuccessTimeoutRef.current = setTimeout(() => {
        setSaveSuccessMessage("");
        saveSuccessTimeoutRef.current = null;
      }, 3000);
      setForm({ name: "", description: "", sortOrder: 0, isActive: true });
      setSelectedAppOptionIds([]);
      setShowForm(false);
    } catch {
      // error handled by UI
    }
  };

  const handleEdit = (accessLevel) => {
    createState.reset();
    updateState.reset();
    setEditingId(accessLevel.accessLevelId);
    setForm({
      name: accessLevel.name || "",
      description: accessLevel.description || "",
      sortOrder: accessLevel.sortOrder ?? 0,
      isActive:
        accessLevel.isActive !== undefined ? accessLevel.isActive : true,
    });
    setSelectedAppOptionIds(
      normalizeIdList(
        accessLevel.appOptionIds ||
          accessLevel.permissions ||
          accessLevel.screenIds,
      ),
    );
    setShowForm(true);
  };

  const handleDelete = (accessLevelId) => {
    const accessLevel = accessLevels.find(
      (a) => a.accessLevelId === accessLevelId,
    );
    setDeleteModal({
      isOpen: true,
      accessLevelId,
      accessLevelName: accessLevel?.name || "this access level",
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteAccessLevel(deleteModal.accessLevelId).unwrap();
      setDeleteModal({
        isOpen: false,
        accessLevelId: null,
        accessLevelName: "",
      });
    } catch (err) {
      setDeleteModal({
        isOpen: false,
        accessLevelId: null,
        accessLevelName: "",
      });
      const errorMessage =
        err?.data?.detail ||
        err?.data?.message ||
        "Failed to delete access level. It may be assigned to users.";
      setErrorModal({ isOpen: true, message: errorMessage });
    }
  };

  const handleCancel = () => {
    resetForm();
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "screens", label: "Screens" },
    { key: "sortOrder", label: "Sort Order" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const rows = accessLevels.map((accessLevel) => {
    const accessLevelScreens = normalizeIdList(
      accessLevel.appOptionIds ||
        accessLevel.permissions ||
        accessLevel.screenIds,
    );
    const previewNames = accessLevelScreens
      .map((id) => getPrettyScreenLabel(screenLookup.get(id)) || id)
      .filter(Boolean);

    return {
      id: accessLevel.accessLevelId,
      name: accessLevel.name,
      screens: (
        <div className="flex flex-wrap gap-1">
          {previewNames.slice(0, 3).map((name) => (
            <span
              key={name}
              className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-200"
            >
              {name}
            </span>
          ))}
          {previewNames.length === 0 ? (
            <span className="text-sm text-slate-400 dark:text-slate-500">
              None
            </span>
          ) : null}
          {previewNames.length > 3 ? (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              +{previewNames.length - 3} more
            </span>
          ) : null}
        </div>
      ),
      sortOrder: accessLevel.sortOrder ?? 0,
      status: (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {accessLevel.isActive ? "Active" : "Inactive"}
        </span>
      ),
      actions: (
        <ActionButtons
          actions={[
            {
              icon: "edit",
              label: "Edit",
              color: "primary",
              onClick: () => handleEdit(accessLevel),
            },
            {
              icon: "delete",
              label: "Delete",
              color: "danger",
              onClick: () => handleDelete(accessLevel.accessLevelId),
            },
          ]}
        />
      ),
    };
  });

  const buildToggleValues = (screenIds) =>
    screenIds.reduce((acc, screenId) => {
      acc[screenId] = selectedAppOptionIds.includes(screenId);
      return acc;
    }, {});

  const renderToggleSection = ({
    title,
    screenItems,
  }) => (
    <div className="space-y-3">
      <div className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {title}
      </div>
      <FeatureToggleGrid
        items={screenItems}
        values={buildToggleValues(screenItems.map((item) => item.name))}
        onChange={handleToggleScreen}
        gridClassName="grid gap-x-12 gap-y-5 md:grid-cols-2 xl:grid-cols-3"
        labelClassName="text-[15px] text-slate-700 dark:text-slate-300"
      />
    </div>
  );
  const featureStatusMessage = useMemo(() => {
    if (!featuresError) return null;
    return "Store features could not be loaded. The screen list is shown without feature filtering.";
  }, [featuresError]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Access Levels"
        subtitle="Manage role metadata and assign screens to each role."
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button size="sm" onClick={() => setShowForm(true)}>
              Add Access Level
            </Button>
          )
        }
      />

      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {editingId ? "Edit Access Level" : "Add Access Level"}
          </h2>
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Name"
                name="name"
                placeholder="POS_MANAGER"
                value={form.name}
                onChange={handleChange}
                required
                helper="Unique identifier, e.g. POS_ADMIN or POS_STAFF"
              />

              <Input
                label="Sort Order"
                name="sortOrder"
                type="number"
                placeholder="0"
                value={form.sortOrder}
                onChange={handleChange}
                helper="Lower numbers appear first"
              />
            </div>

            <Checkbox
              label="Active"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
            />

            <div className="space-y-5">
              <div className="mb-1 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    Screen Access
                  </h3>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Assign the screens this role can see and open.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={handleSelectAllScreens}
                    disabled={!visibleScreenIds.length}
                  >
                    Select All
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={handleClearAllScreens}
                    disabled={!selectedAppOptionIds.length}
                  >
                    Clear All
                  </Button>
                </div>
              </div>

              {featuresLoading || screensLoading || screensFetching ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Loading screen catalog...
                </p>
              ) : null}

              {featureStatusMessage ? (
                <Alert tone="error">{featureStatusMessage}</Alert>
              ) : null}

              {screensError ? (
                <Alert tone="error">
                  {screensQueryError?.data?.detail ||
                    "Failed to load screens for this access level."}
                </Alert>
              ) : null}

              {!screensLoading && !screensFetching && !screensError ? (
                <div className="space-y-8">
                  {hiddenScreenCount > 0 ? (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                      {hiddenScreenCount} screens are hidden because the current
                      business does not have the required feature enabled.
                    </div>
                  ) : null}

                  {screenTree.groups.length > 0 ? (
                    <div className="space-y-8">
                      {screenTree.groups.map((group) => {
                        const screenItems = group.children.map((screen) => ({
                          name: getScreenId(screen),
                          label:
                            getPrettyScreenLabel(screen) ||
                            screen.name ||
                            screen.screenKey ||
                            screen.route,
                        }));

                        return renderToggleSection({
                          title: group.parent.name,
                          screenItems,
                        });
                      })}
                    </div>
                  ) : null}

                  {screenTree.standaloneScreens.length > 0 ? (
                    <div>
                      {renderToggleSection({
                        title: "Standalone Screens",
                        screenItems: screenTree.standaloneScreens.map(
                          (screen) => ({
                            name: getScreenId(screen),
                            label:
                              getPrettyScreenLabel(screen) ||
                              screen.name ||
                              screen.screenKey ||
                              screen.route,
                          }),
                        ),
                      })}
                    </div>
                  ) : null}

                  {!screenTree.groups.length &&
                  !screenTree.standaloneScreens.length ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      No screens available for this business.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>

            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save access level."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Access Level
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700 dark:border-slate-700 dark:text-slate-300">
            All Access Levels
          </div>
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading access levels...
              </p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load access levels."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <Table
                columns={columns}
                rows={rows}
                emptyText="No access levels yet."
              />
            )}
          </div>
        </Card>
      )}

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({
            isOpen: false,
            accessLevelId: null,
            accessLevelName: "",
          })
        }
        title="Delete Access Level"
        confirmText="Delete"
        confirmVariant="primary"
        onConfirm={confirmDelete}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.accessLevelName}</strong>?
        </p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          This action cannot be undone. The access level cannot be deleted if
          it's assigned to users.
        </p>
      </Modal>

      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p>{errorModal.message}</p>
      </Modal>

      {saveSuccessMessage && (
        <div className="fixed bottom-4 right-4 z-50 w-[min(92vw,28rem)] shadow-xl">
          <Alert
            tone="success"
            className="border-emerald-200 bg-emerald-50/95 backdrop-blur"
            onClose={() => {
              if (saveSuccessTimeoutRef.current) {
                clearTimeout(saveSuccessTimeoutRef.current);
                saveSuccessTimeoutRef.current = null;
              }
              setSaveSuccessMessage("");
            }}
          >
            {saveSuccessMessage}
          </Alert>
        </div>
      )}
    </div>
  );
};

export default AccessLevels;
